import React, { useEffect, useRef, useState } from "react";
import PackageCard from "../../components/PackageCard";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import { buildPackageBookingLink } from "../../utils/packageBookingLink";
import {
  bookingScopeFromReturnTo,
  markPackagePurchasedDuringBooking,
} from "../../utils/bookingDraft";
import "./PackagePurchaseConfirmation.css";

/**
 * Stripe sends a buyer here after paying for a package:
 *   /packages/purchase-confirmation?session_id=...&purchase_id=...
 *
 * This page is the ONLY thing that activates a package purchase: the backend
 * `GET /api/packages/:purchaseId/confirm` verifies the Stripe session, flips the purchase to
 * active, increments the package's purchase count and emails the buyer.
 *
 * That coupling is why this page must exist — without it the redirect lands on nothing, the
 * purchase stays `pending` forever, no email is sent, and the customer has paid for a package
 * they cannot use.
 *
 * It must stay reachable WITHOUT an account: a guest purchase is identified by the email
 * entered at checkout, not by a session.
 */

const API_BASE = String(process.env.REACT_APP_API_BASE_URL || "").replace(
  /\/+$/,
  "",
);

const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * The confirm endpoint answers in two shapes:
 *  - freshly activated: { success, message, purchase: { id, packageName, totalSessions, … } }
 *  - already active:    { success, message, purchase: <full purchase document> }
 * Both are normalised here so the page renders one thing.
 */
const normalizePurchase = (payload) => {
  if (!payload) return null;
  const raw = payload.purchase || payload;

  return {
    id: raw.id || raw._id || null,
    packageName:
      raw.packageName || raw.packageSnapshot?.name || "your package",
    totalSessions:
      raw.totalSessions ?? raw.packageSnapshot?.totalSessions ?? null,
    sessionsRemaining:
      typeof raw.sessionsRemaining === "number" ? raw.sessionsRemaining : null,
    validUntil: raw.validUntil || null,
    status: raw.status || null,
    shopName: raw.shopId?.name || raw.shopId?.shopName || null,
    email: raw.customerEmail || null,
    isGuest: Boolean(raw.isGuestPurchase),
    // THE NUMBER, so the page that says "payment successful" is also the page that hands over the thing
    // needed to USE the package. The freshly-activated shape and the full document both carry it; a purchase
    // made before numbers existed carries none, and the card renders without the number band rather than
    // inventing one.
    accountNumber: raw.accountNumber || null,
    // THE CARDHOLDER, as the card names them. "Valued Customer" is a GREETING and is skipped here: printed on
    // a cardholder line it reads as an unfinished card. What is left is the real name or the email address,
    // which is the true holder identity — the package belongs to that mailbox.
    customerName: realHolderName(raw),
    // THE DOCUMENT ITSELF, for the "book with this package" link (plan item 2.12). The link needs the shop,
    // the covered services and the subscribed professional, none of which belong on a summary line — but all
    // of which the server already sent. Kept here rather than re-fetched, because this page has no token for a
    // guest and the confirm response is the one place the whole document is guaranteed to be present.
    raw,
  };
};

const PLACEHOLDER_NAMES = new Set(["valued customer", "guest", "customer"]);

/** A real name, or the mailbox. Never the placeholder. */
function realHolderName(raw) {
  const candidates = [
    // THE PURCHASE'S OWN FIELDS: `customerFirstName` and an optional `customerLastName`. There is no
    // `customerName` on the model, which is why this kept landing on the email address.
    [raw.customerFirstName, raw.customerLastName].filter(Boolean).join(" ").trim(),
    raw.customer?.firstName,
  ];

  for (const candidate of candidates) {
    const name = String(candidate || "").trim();
    if (name && !PLACEHOLDER_NAMES.has(name.toLowerCase())) return name;
  }

  return raw.customerEmail || null;
}

const PackagePurchaseConfirmation = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const purchaseId = searchParams.get("purchase_id");
  /**
   * 22G — THIS SAME PAGE IS WHERE LOST PACKAGE SESSIONS ARE BOUGHT BACK, so it must say which one it was.
   *
   * The redeem endpoint returns the payer here with `redeemed=1`. Without this the page would announce
   * "Payment successful — your package is active and ready to use" to somebody who paid to RESTORE sessions
   * they had lost: the payment did succeed, but the sentence would be describing a purchase that did not
   * happen, and the customer would not know whether their sessions came back.
   */
  const redeemedSessions = searchParams.get("redeemed") === "1";
  const navigate = useNavigate();

  /**
   * Where to send them after a purchase made from inside a booking. Validated here too, not only on the server:
   * an internal path and nothing else, so a crafted link cannot turn this page into an open redirect.
   */
  const rawReturnTo = searchParams.get("returnTo") || "";
  const safeReturnTo =
    rawReturnTo.startsWith("/") && !rawReturnTo.startsWith("//") ? rawReturnTo : "";

  const [state, setState] = useState("verifying"); // verifying | success | error
  const [purchase, setPurchase] = useState(null);
  /**
   * 22G — WHAT THIS PAYMENT RESTORED, AND WHAT IS STILL HELD.
   *
   * A redemption is priced for the holds it was bought for, and a package can carry others from earlier
   * cancellations (reported 2026-10-16: a cancellation quoted at $40 took the customer to a payment for $80 — a
   * figure nobody had shown them). The charge now matches the figure, and this is where the customer is told
   * that other sessions are still held: the page must not imply the package is fully square when it is not.
   */
  const [redemption, setRedemption] = useState(null);
  /** `{units, amount, currency}` when the package STILL has held sessions after this payment, else null. */
  const [alsoOwed, setAlsoOwed] = useState(null);
  const [error, setError] = useState("");
  /** Whether the browser refused `window.close()` — a dead button is worse than a sentence explaining itself. */
  const [tabCloseBlocked, setTabCloseBlocked] = useState(false);

  // React 18 StrictMode runs effects twice in development. The endpoint is idempotent so a
  // second call is harmless, but it would answer "already activated" and replace the clearer
  // first response. This keeps the first answer.
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    if (hasRequestedRef.current) return;
    hasRequestedRef.current = true;

    if (!sessionId || !purchaseId) {
      setState("error");
      setError(
        "This confirmation link is incomplete. Open the link from your Stripe receipt, or contact the shop if you were charged and no package appeared.",
      );
      return;
    }

    const confirmPurchase = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/packages/${encodeURIComponent(
            purchaseId,
          )}/confirm?session_id=${encodeURIComponent(sessionId)}`,
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          // The backend sends a human-readable `message` for the known failures
          // (payment_not_completed, purchase_not_found, missing_session_id).
          // Never show a raw status code on its own — the buyer needs to know what to do.
          setState("error");
          setError(
            data.message ||
              "We could not confirm this purchase. If your card was charged, contact the shop with your Stripe receipt and they can activate the package manually.",
          );
          return;
        }

        setPurchase(normalizePurchase(data));
        // The redemption branch reports what came back and whether anything else is still held. Absent on a
        // purchase, which is why it is read rather than assumed.
        setRedemption(data.redemption || null);
        setAlsoOwed(data.alsoOwed || null);
        setState("success");
        /**
         * 2.8a — TELL THE BOOKING THEY NOW HOLD A PACKAGE (2026-10-27).
         *
         * Written here rather than at the buy button, because this is the first moment the payment is VERIFIED:
         * a cancelled or failed checkout must not close the offers. The booking page reads it on its next mount
         * and stops showing "Packages you can buy", so the customer is never invited to buy again what they have
         * just paid for. Cleared with the draft when the booking is confirmed.
         */
        const purchaseScope = bookingScopeFromReturnTo(safeReturnTo);
        if (purchaseScope) markPackagePurchasedDuringBooking(purchaseScope);
      } catch (err) {
        setState("error");
        setError(
          "We could not reach the server to confirm your purchase. Your payment may still have gone through — reload this page, and contact the shop if the problem continues.",
        );
      }
    };

    confirmPurchase();
  }, [sessionId, purchaseId, safeReturnTo]);

  /**
   * 22G — AND IT CLOSES ITSELF (user, 2026-10-14: *"open the stripe payment page in a new tab and after payment
   * close it and silently update the session to be usable"*).
   *
   * The delay is deliberate: the customer must be able to READ that their sessions came back before the page
   * disappears under them. Four seconds is long enough to see the heading and short enough that nobody reaches
   * for the close button first.
   *
   * If the browser refuses (the tab was not opened by script, or a policy blocks it), nothing happens — the
   * page simply stays, and the button below says what to do. There is no way to detect the refusal, so the
   * fallback is the visible sentence rather than a state that claims the tab is gone.
   */
  useEffect(() => {
    if (!(state === "success" && redeemedSessions)) return undefined;
    const timer = setTimeout(() => window.close(), 4000);
    return () => clearTimeout(timer);
  }, [state, redeemedSessions]);

  /**
   * WHERE "BOOK WITH THIS PACKAGE" GOES (plan item 2.12).
   *
   * Built from the purchase document the confirm call returned, and only when it can actually be built —
   * `/booking` needs a shop, a covered service and a subscribed professional, and a package missing any of
   * them gets no button rather than a link that fails on arrival.
   */
  const bookLink = purchase?.raw
    ? buildPackageBookingLink(purchase.raw)
    : { ok: false, reason: "" };

  return (
    <div className="ppc-page">
      <CustomerHeader />

      <main className="ppc-main">
        {state === "verifying" && (
          <div className="ppc-card ppc-card--pending" aria-live="polite">
            <div className="ppc-spinner" aria-hidden="true" />
            <h1 className="ppc-title">Confirming your purchase…</h1>
            <p className="ppc-text">
              Please keep this page open while we activate your package.
            </p>
          </div>
        )}

        {state === "success" && purchase && (
          <div className="ppc-card ppc-card--success" aria-live="polite">
            <div className="ppc-badge" aria-hidden="true">
              ✓
            </div>
            <h1 className="ppc-title">
              {redeemedSessions ? "Sessions restored" : "Payment successful"}
            </h1>
            <p className="ppc-text">
              {redeemedSessions
                ? redemption?.units
                  ? `${redemption.units} session(s) are back on the package, so you can book with it again.`
                  : "Your sessions are back on the package, so you can book with it again."
                : "Your package is active and ready to use."}
            </p>

            {/* WHAT IS STILL HELD. Without this the page says "Sessions restored" to somebody who still has
                sessions to pay for, and they find out when they try to book (reported 2026-10-16). */}
            {redeemedSessions && alsoOwed && alsoOwed.units > 0 && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "12px 14px",
                  background: "#fef3c7",
                  border: "1px solid #fbbf24",
                  borderRadius: "8px",
                  color: "#92400e",
                  fontSize: "13px",
                  lineHeight: 1.6,
                  textAlign: "left",
                }}
              >
                <strong>
                  {alsoOwed.units} more session(s) from this package are still held
                </strong>{" "}
                — that is a separate {alsoOwed.currency === "USD" ? "$" : ""}
                {Number(alsoOwed.amount || 0).toFixed(2)} to restore, from a cancellation you made earlier. This
                payment did not include them. Open the booking page to pay for them whenever you want, or they
                end with the package when it expires.
              </div>
            )}

            {/* 22G — THE PAYMENT TAB CLOSES ITSELF (user, 2026-10-14: *"open the stripe payment page in a new
                tab and after payment close it and silently update the session to be usable"*). The booking page
                underneath refreshes the moment it regains focus, so closing this tab IS the update.

                `window.close()` only works on a window script opened, which is exactly how this tab was opened
                — and when it is not (someone pasted the URL, or the browser refuses), the page stays and says
                so, rather than showing a dead "closed" state. */}
            {redeemedSessions && (
              <button
                type="button"
                className="ppc-close-tab"
                style={{
                  marginTop: "12px",
                  padding: "10px 16px",
                  background: "#b45309",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
                onClick={() => {
                  window.close();
                  // If the browser refused (this tab was not opened by script), say what to do instead.
                  setTabCloseBlocked(true);
                }}
              >
                {tabCloseBlocked
                  ? "Your sessions are restored — you can close this tab"
                  : "Close this tab and carry on booking"}
              </button>
            )}

            <div className="ppc-summary">
              <div className="ppc-summary-name">{purchase.packageName}</div>
              {/* THE CARD, ABOVE THE DETAILS (user, 2026-10-09). This is the one moment a buyer is certain
                  to be looking, and the number is the one thing they must keep — the email carries it, but
                  "I already closed the email" is not a reason to lose a package. */}
              <PackageCard
                accountNumber={purchase.accountNumber}
                holderName={purchase.customerName}
                validUntil={purchase.validUntil}
                totalSessions={purchase.totalSessions}
                shopName={purchase.shopName}
                packageName={purchase.packageName}
              />

              <dl className="ppc-details">
                {purchase.totalSessions != null && (
                  <div className="ppc-detail">
                    <dt>Sessions included</dt>
                    <dd>{purchase.totalSessions}</dd>
                  </div>
                )}

                {purchase.sessionsRemaining != null && (
                  <div className="ppc-detail">
                    <dt>Sessions remaining</dt>
                    <dd>{purchase.sessionsRemaining}</dd>
                  </div>
                )}

                {formatDate(purchase.validUntil) && (
                  <div className="ppc-detail">
                    <dt>Valid until</dt>
                    <dd>{formatDate(purchase.validUntil)}</dd>
                  </div>
                )}

                {purchase.shopName && (
                  <div className="ppc-detail">
                    <dt>Shop</dt>
                    <dd>{purchase.shopName}</dd>
                  </div>
                )}

                {purchase.id && (
                  <div className="ppc-detail">
                    <dt>Purchase reference</dt>
                    <dd className="ppc-mono">{purchase.id}</dd>
                  </div>
                )}
              </dl>
            </div>

            {purchase.email && (
              <p className="ppc-note">
                A confirmation has been sent to{" "}
                <strong>{purchase.email}</strong>.
                {purchase.isGuest && (
                  <>
                    {" "}
                    Keep that email — it is the record of this purchase, and
                    no account is needed to use the package. If you would like
                    it saved to an account, sign up with this same address and
                    it will be waiting for you.
                  </>
                )}
              </p>
            )}

            <div className="ppc-actions">
              {/* BACK TO THE BOOKING THEY WERE FILLING IN (2026-10-26). Present only when the purchase was
                  started from inside a booking: the server accepted `returnTo` as an internal path and carried
                  it through Stripe as this page's query. They land back on the same shop, professional and
                  service, with their entries restored from the draft, and the summary's "Use my package" button
                  is what applies it. */}
              {safeReturnTo && (
                <button
                  type="button"
                  className="ppc-btn ppc-btn--primary"
                  onClick={() => navigate(safeReturnTo)}
                >
                  Continue my booking
                </button>
              )}
              {/* PLAN ITEM 2.12 (2026-10-23). The moment the customer has paid is the moment they want to USE
                  the thing — and until now the only two doors out of this page were "View packages" and
                  "Back to home", so using what they had just bought meant finding the shop again. This goes
                  straight to `/booking` with the purchase and its first covered service preselected. It
                  spends nothing: the session is taken when the booking is confirmed, not when this is
                  pressed, so an abandoned booking costs no session. Rendered only when the link can actually
                  be built from what the server sent; otherwise the two links below are the honest offer. */}
              {bookLink.ok && (
                <Link className="ppc-btn ppc-btn--primary" to={bookLink.href}>
                  Book with this package
                </Link>
              )}
              <Link className="ppc-btn ppc-btn--primary" to="/packages">
                View packages
              </Link>
              <Link className="ppc-btn" to="/">
                Back to home
              </Link>
            </div>
          </div>
        )}

        {state === "error" && (
          <div className="ppc-card ppc-card--error" role="alert">
            <div className="ppc-badge ppc-badge--error" aria-hidden="true">
              !
            </div>
            <h1 className="ppc-title">We couldn&apos;t confirm your purchase</h1>
            <p className="ppc-text">{error}</p>

            <div className="ppc-actions">
              <Link className="ppc-btn ppc-btn--primary" to="/packages">
                View packages
              </Link>
              <Link className="ppc-btn" to="/help">
                Get help
              </Link>
            </div>
          </div>
        )}
      </main>

      <CustomerFooter />
    </div>
  );
};

export default PackagePurchaseConfirmation;
